import { useState, useRef, useEffect } from 'react';

/**
 * ChecklistFilterPopover — unified secondary-filter button for the
 * checklist tab. Mirrors the visual pattern of the inventory's
 * HierarchicalFilterChip so the two pages share a filter language.
 *
 * Sections: Status (multi-select buckets), Confidence (Strong/Possible/
 * Weak buckets). The earlier Item Type section was dropped to match
 * the paralegal-facing row UI pass — internal classification axes
 * (filename/narrative/etc.) don't show on rows so they shouldn't
 * show in the filter either. The earlier 0–100% slider was replaced
 * with three confidence buckets so the filter speaks the same
 * language as the row pills.
 *
 * Score-range translation is internal: clicking Strong sets
 * [80, 100], Possible sets [40, 80), Weak sets [0, 40). Multi-select
 * unions the ranges; the call site still uses scoreRange under the
 * hood so server-side filter math is unchanged.
 */

// Confidence-bucket → score-range mapping. Mirrors the
// confidenceLabel() helper in ChecklistTab — keep these in sync.
const CONFIDENCE_BUCKETS = [
  { value: 'strong',   label: 'Strong match',   icon: '✓', min: 80, max: 100 },
  { value: 'possible', label: 'Possible match', icon: '~', min: 40, max: 79  },
  { value: 'weak',     label: 'Weak match',     icon: '!', min: 0,  max: 39  },
];

// Translate a {min, max}% pair into the set of selected buckets. A
// bucket is selected when its range is fully contained in the active
// filter range.
function rangeToBuckets(scoreRange) {
  const [lo, hi] = scoreRange;
  if (lo === 0 && hi === 100) return new Set();
  const selected = new Set();
  for (const b of CONFIDENCE_BUCKETS) {
    if (b.min >= lo && b.max <= hi) selected.add(b.value);
  }
  return selected;
}

// Compute the score-range that covers the union of the selected
// buckets. Empty selection = full range (no filter).
function bucketsToRange(buckets) {
  if (!buckets || buckets.size === 0) return [0, 100];
  let lo = 100, hi = 0;
  for (const b of CONFIDENCE_BUCKETS) {
    if (!buckets.has(b.value)) continue;
    if (b.min < lo) lo = b.min;
    if (b.max > hi) hi = b.max;
  }
  return [lo, Math.max(hi, lo)];
}

export default function ChecklistFilterPopover({
  activeBuckets,
  bucketOptions,
  onBucketToggle,
  scoreRange,
  scoreDragRange: _scoreDragRange,  // unused after slider removal
  onScoreRangeChange,
  onScoreDragChange: _onScoreDragChange,  // unused after slider removal
  onResetAll,
}) {
  const scoreActive = scoreRange[0] > 0 || scoreRange[1] < 100;
  const bucketActive = activeBuckets && activeBuckets.size > 0;
  const selectedConfidence = rangeToBuckets(scoreRange);
  const [open, setOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(
    bucketActive ? 'bucket'
      : scoreActive ? 'score'
      : 'bucket'
  );
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const activeChips = [];
  if (activeBuckets) {
    for (const b of activeBuckets) {
      const opt = (bucketOptions || []).find(o => o.value === b);
      activeChips.push({
        key: `bucket:${b}`,
        icon: opt?.icon || '•',
        label: opt?.label || b,
        onClear: () => onBucketToggle(b),
        ariaLabel: `Clear ${opt?.label || b} status filter`,
      });
    }
  }
  // Confidence chips — one per selected bucket. Each clears just its
  // bucket from the range; remaining buckets recompute the new range.
  for (const cb of CONFIDENCE_BUCKETS) {
    if (!selectedConfidence.has(cb.value)) continue;
    activeChips.push({
      key: `confidence:${cb.value}`,
      icon: cb.icon,
      label: cb.label,
      onClear: () => {
        const next = new Set(selectedConfidence);
        next.delete(cb.value);
        onScoreRangeChange(bucketsToRange(next));
      },
      ariaLabel: `Clear ${cb.label} filter`,
    });
  }

  const toggleSection = (s) => setExpandedSection(prev => prev === s ? null : s);

  return (
    <div className="hfilter-wrap" ref={ref}>
      <button
        type="button"
        className={`filter-chip ${activeChips.length > 0 ? 'filter-chip--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        title="Filters"
      >
        🔽 Filters{activeChips.length > 0 && (
          <span className="hfilter-badge">{activeChips.length}</span>
        )}
      </button>

      {activeChips.map(c => (
        <span key={c.key} className="hfilter-active-chip">
          <span className="hfilter-active-chip-icon">{c.icon}</span>
          {c.label}
          <span
            className="filter-chip-clear"
            role="button"
            tabIndex={0}
            onClick={c.onClear}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); c.onClear(); } }}
            aria-label={c.ariaLabel}
          >
            ✕
          </span>
        </span>
      ))}

      {open && (
        <div className="hfilter-popover">
          {/* Status — multi-select buckets (replaces the old inline pill row) */}
          {bucketOptions && bucketOptions.length > 0 && (
            <div className="hfilter-section">
              <button
                type="button"
                className="hfilter-section-header"
                onClick={() => toggleSection('bucket')}
              >
                <span>🗂 Status{activeBuckets && activeBuckets.size > 0 ? ` (${activeBuckets.size})` : ''}</span>
                <span className="hfilter-section-caret">
                  {expandedSection === 'bucket' ? '▾' : '▸'}
                </span>
              </button>
              {expandedSection === 'bucket' && (
                <div className="hfilter-section-body">
                  {bucketOptions.map(o => {
                    const selected = activeBuckets?.has(o.value);
                    return (
                      <button
                        key={o.value}
                        type="button"
                        className={`hfilter-option ${selected ? 'hfilter-option--selected' : ''}`}
                        onClick={() => onBucketToggle(o.value)}
                        title={selected ? 'Click to remove from filter' : 'Click to add to filter'}
                      >
                        <span>{o.icon ? `${o.icon} ` : ''}{o.label}</span>
                        <span className="hfilter-option-meta">
                          <span className="hfilter-count">{o.count}</span>
                          {selected && <span className="filter-chip-check">✓</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Confidence — multi-select bucket filter. Replaces the
              prior 0–100% range slider so the filter speaks the same
              language as the row pills (Strong/Possible/Weak). */}
          <div className="hfilter-section">
            <button
              type="button"
              className="hfilter-section-header"
              onClick={() => toggleSection('score')}
            >
              <span>🎯 Confidence{selectedConfidence.size > 0 ? ` (${selectedConfidence.size})` : ''}</span>
              <span className="hfilter-section-caret">
                {expandedSection === 'score' ? '▾' : '▸'}
              </span>
            </button>
            {expandedSection === 'score' && (
              <div className="hfilter-section-body">
                {CONFIDENCE_BUCKETS.map(cb => {
                  const selected = selectedConfidence.has(cb.value);
                  return (
                    <button
                      key={cb.value}
                      type="button"
                      className={`hfilter-option ${selected ? 'hfilter-option--selected' : ''}`}
                      onClick={() => {
                        const next = new Set(selectedConfidence);
                        if (selected) next.delete(cb.value);
                        else next.add(cb.value);
                        onScoreRangeChange(bucketsToRange(next));
                      }}
                      title={selected ? 'Click to remove from filter' : 'Click to add to filter'}
                    >
                      <span>{cb.icon} {cb.label}</span>
                      <span className="hfilter-option-meta">
                        {selected && <span className="filter-chip-check">✓</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {activeChips.length > 0 && (
            <div className="hfilter-footer">
              <button
                type="button"
                className="hfilter-clear-all"
                onClick={() => { onResetAll(); setOpen(false); }}
              >
                Reset filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
